import { jwtDecode } from "jwt-decode";

import type { StoredSession } from "./session-storage";

/** Why a session ended, so the app can tell the user. */
export type SessionEndReason =
  | "user"
  | "expired"
  | "unauthorized"
  | "unsupportedRole";

export type RefreshPath = "current" | "password" | "sso" | "none";

// Refreshing slightly early keeps a request from leaving with a token that expires in flight.
const EXPIRY_MARGIN_MS = 30_000;

/** Whether the token expires within the margin; a token without an expiry counts as expired. */
export const isAccessTokenExpired = (
  accessToken: string,
  nowMs: number,
): boolean => {
  const { exp } = jwtDecode<{ exp?: number }>(accessToken);
  return exp === undefined || exp * 1000 - EXPIRY_MARGIN_MS <= nowMs;
};

/** How to get a usable access token for the session at this moment. */
export const refreshPathFor = (
  session: StoredSession,
  nowMs: number,
): RefreshPath => {
  if (!isAccessTokenExpired(session.accessToken, nowMs)) return "current";
  if (session.provider === "sso") return "sso";
  return session.refreshToken ? "password" : "none";
};

// The query parameter the app reads after Auth0 redirects back from an SSO logout.
export const SIGNED_OUT_PARAM = "signedOut";

/** Where Auth0 returns to after an SSO logout, carrying why the session ended. */
export const signedOutReturnUrl = (
  origin: string,
  reason: SessionEndReason,
): string => `${origin}/?${SIGNED_OUT_PARAM}=${reason}`;
